import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ContextMenu.css';

export interface ContextMenuItem {
  key: string;
  label: string;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
}

interface ContextMenuProps {
  /** Viewport coordinates of the right-click (event.clientX / clientY). */
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
  /** Optional line above the items, for example "3 users selected". */
  subtitle?: string;
}

const VIEWPORT_MARGIN = 8;

/**
 * A right-click menu. Behaviour follows the inbox's inline menu (InboxPage.tsx):
 * fixed at the pointer, clamped inside the viewport, dismissed by an outside
 * mousedown, any scroll, a resize, or Escape. Unlike the inbox menu it measures
 * itself instead of assuming a size, and it portals to document.body so a
 * transformed or overflow-hidden ancestor cannot clip or offset it.
 */
const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, items, onClose, subtitle }) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ left: number; top: number }>({ left: x, top: y });

  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const left = Math.max(VIEWPORT_MARGIN, Math.min(x, window.innerWidth - width - VIEWPORT_MARGIN));
    const top = Math.max(VIEWPORT_MARGIN, Math.min(y, window.innerHeight - height - VIEWPORT_MARGIN));
    setPosition({ left, top });
  }, [x, y, items.length, subtitle]);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (menuRef.current && e.target instanceof Node && menuRef.current.contains(e.target)) return;
      onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const handleDismiss = () => onClose();

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('scroll', handleDismiss, true);
    window.addEventListener('resize', handleDismiss);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('scroll', handleDismiss, true);
      window.removeEventListener('resize', handleDismiss);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={menuRef}
      className="tm-context-menu"
      role="menu"
      style={{ left: position.left, top: position.top }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {subtitle ? <div className="tm-context-menu-subtitle">{subtitle}</div> : null}
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          role="menuitem"
          className={`tm-context-menu-item${item.danger ? ' tm-context-menu-item--danger' : ''}`}
          disabled={item.disabled}
          onClick={() => {
            onClose();
            item.onSelect();
          }}
        >
          {item.label}
        </button>
      ))}
    </div>,
    document.body
  );
};

export default ContextMenu;
